import { VerifiedUser } from '@mui/icons-material';
import {
  Box,
  FormGroupLabel,
  Icon,
  RadioSelectableCard,
  Stack,
  Typography,
  useTheme,
} from '@qbe/ads-core';
import React, { useMemo } from 'react';

import AmexIcon from '~/components-ds/PaymentIcons/AmexIcon';
import MastercardIcon from '~/components-ds/PaymentIcons/MastercardIcon';
import VisaIcon from '~/components-ds/PaymentIcons/VisaIcon';
import { Option, useAppContent, useOptionSelect } from '~/hooks';
import { PaymentMethodOptions } from '~/types/payments';

import { ContextualHelpWithButton } from '../../components-ds/ContextualHelp/ContextualHelpWithButton';

const toValue = {
  [PaymentMethodOptions.CreditCard]: 'Credit/debit',
  [PaymentMethodOptions.BankAccount]: 'Bank account',
};

interface PaymentMethodProps {
  paymentMethods?: PaymentMethodOptions[];
}

export const PaymentMethod = ({
  paymentMethods = [PaymentMethodOptions.CreditCard],
}: PaymentMethodProps) => {
  const { getTextContent, getJSXContent } = useAppContent({
    basePath: 'dsme.paymentMethodComponent',
  });

  const theme = useTheme();
  const options: Option[] = Object.values(paymentMethods).map((option) => ({
    id: option,
    value: toValue[option],
  }));
  const { selectedIDs, onSelectID } = useOptionSelect({
    options,
    defaultOptions: [0],
  });

  const radioList = useMemo(() => {
    const onSelect = (id: string) => {
      if (!selectedIDs.includes(id)) {
        onSelectID(id);
      }
    };

    return options.map(({ id, value }) => {
      const isSelected = !!selectedIDs.find((o) => o === id);

      const res = {
        id,
        label: value,
        onChange: () => onSelect(id),
        customComponent: <VisaIcon fontSize="large" />,
        isSelected,
      };

      if (id === PaymentMethodOptions.CreditCard) {
        res.customComponent = (
          <Stack direction="row" marginTop="-0.5em" marginBottom="-0.5em" spacing={1}>
            <VisaIcon fontSize="large" />
            <MastercardIcon fontSize="large" />
            <AmexIcon fontSize="large" />
          </Stack>
        );
      }

      return res;
    });
  }, [options, selectedIDs, onSelectID]);

  return (
    <Box width="100%" maxWidth="400px" textAlign="center">
      <FormGroupLabel
        title={getTextContent('title')}
        legendElementOveride
        alignCenter
        titlePrefix={
          <Icon
            muiIcon={VerifiedUser}
            // TODO - https://qbe-appservices.atlassian.net/browse/AD1-445
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            color={theme.color.icon.info}
            size={32}
          />
        }
        titleSuffix={
          <ContextualHelpWithButton tracking="help: How would you like to pay?">
            <Typography>{getJSXContent('content.contextualHelper.description')}</Typography>
          </ContextualHelpWithButton>
        }
      />

      <RadioSelectableCard
        title="" // TODO - delete prop: https://qbe-appservices.atlassian.net/browse/AD1-460
        data-testid="payment-method"
        name="method"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        radioList={radioList as any}
        direction="vertical"
      />
    </Box>
  );
};
